import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FaBox } from 'react-icons/fa';
import { LiaPalletSolid } from 'react-icons/lia';
import { useLocation } from 'react-router';
import { CombinedError } from 'urql';
import { getLoadingMarket } from 'hooks/market/loadingMarket';
import useIsMarketQueryPaused from 'hooks/market/useIsMarketQueryPaused';
import useMarketInput from 'hooks/market/useMarketInput';
import useMarketRefetchResults from 'hooks/market/useMarketRefetchResults';
import useOfflineQuery from 'hooks/useOfflineQuery';
import { marketQueryBuyerID, marketQueryTruckTypeUUID } from 'utils/isMarketStateSet';
import {
  BuyersMarketResultsQuery,
  CardFragment as Card,
  Market,
  MarketFilters,
  MarketMetric,
  MarketSectionResults,
  MarketShippingFragment,
  PageInfo,
  RefetchType,
  useBuyersMarketInfoQuery,
  useBuyersMarketResultsQuery,
} from 'generated/graphql';
import { useStitchBuyerMarketResults } from '../hooks/useStitchBuyersMarketResult';
import { getUniqueProductId, isBuyersMarketPath } from '../utils/marketUtils';

interface BuyersMarketContextProps {
  fetching: boolean;
  filters?: MarketFilters;
  infoFetching: boolean;
  isMarketQueryPaused: boolean;
  marketError?: CombinedError;
  pageInfo?: Partial<PageInfo>;
  promotions?: Card[];
  sections?: MarketSectionResults[];
  shipping?: MarketShippingFragment;
  title?: string;
  breakingPointId: string;
  loadingMarket: boolean;
  refresh: () => void;
  refetchResults: () => void;
}

export const MARKET_METRIC_OPTIONS = [
  { value: MarketMetric.Pallets, icon: <LiaPalletSolid size={16} /> },
  { value: MarketMetric.Units, icon: <FaBox size={16} /> },
];

export const Context = createContext<BuyersMarketContextProps | undefined>(undefined);
const BREAKING_POINT = 20;

const getBreakingPointId = (sections: MarketSectionResults[] | undefined) => {
  if (!sections) return '0';

  const productIds =
    sections?.flatMap((section) =>
      section.sellers.flatMap((seller) =>
        seller.types.flatMap((type) =>
          type.groups.flatMap((group) =>
            group.products.map((product) => ({ productId: product.id, sellerId: product.seller.id })),
          ),
        ),
      ),
    ) ?? [];

  const productInfo = productIds?.length > BREAKING_POINT ? productIds[productIds.length - BREAKING_POINT] : null;
  return productInfo ? getUniqueProductId(productInfo.sellerId, productInfo.productId) : '0';
};

export const BuyersMarketProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { marketInput } = useMarketInput();
  const [stableLoading, setStableLoading] = useState(false);

  const isMarketQueryPaused = useIsMarketQueryPaused();
  const isCatalogMarketRoute = pathname.includes('catalog-market');

  /** Buyers market data - products, pageInfo */
  const [marketResult, handleResfreshMarketResult] = useBuyersMarketResultsQuery({
    variables: {
      input: {
        ...marketInput!,
        buyerID: marketQueryBuyerID(marketInput?.buyerID),
        truckTypeUUID: marketQueryTruckTypeUUID(marketInput?.truckTypeUUID),
      },
    },
    pause: isMarketQueryPaused || isCatalogMarketRoute,
  });

  // Refetch market results on input changes, auto refresh, or pagination reset
  useMarketRefetchResults(handleResfreshMarketResult);

  const pageInfo = useMemo(() => marketResult.data?.market?.results.page, [marketResult.data]);
  const { marketQuery } = useStitchBuyerMarketResults(marketResult.data as BuyersMarketResultsQuery);

  const resultsFetching = marketResult.fetching || marketResult.stale;

  useEffect(() => {
    if (resultsFetching) {
      setStableLoading(true);
    } else {
      const timer = setTimeout(() => {
        setStableLoading(false);
      }, 200); // absorbs rapid fetching/stale toggles

      return () => clearTimeout(timer);
    }
  }, [resultsFetching]);

  const breakingPointId = useMemo(
    () => getBreakingPointId(marketQuery?.market?.results?.sections as MarketSectionResults[]),
    [marketQuery],
  );

  const pageNumber = marketInput?.paginationOption?.pageNumber ?? 1;
  const sections = marketQuery?.market?.results?.sections as MarketSectionResults[] | undefined;
  const loadingMarket = getLoadingMarket(stableLoading, pageNumber, sections?.length ?? 0);

  /** Buyers market info - filters, promotions, shipping methods */
  const [marketInfoResult, handleResfreshMarketInfo] = useOfflineQuery(
    'buyers-market-info-query',
    useBuyersMarketInfoQuery,
    {
      variables: {
        input: {
          ...marketInput!,
          buyerID: marketQueryBuyerID(marketInput?.buyerID),
          truckTypeUUID: marketQueryTruckTypeUUID(marketInput?.truckTypeUUID),
          paginationOption: { pageNumber: 1, pageSize: 25, ids: [], type: RefetchType.Unknown },
        },
      },
      pause: isMarketQueryPaused || isCatalogMarketRoute,
    },
  );
  const infoFetching = marketInfoResult.fetching;

  // Refetch when user navigates back to market from any other page so they see fresh data
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    const isBuyersMarketRoute = isBuyersMarketPath(pathname);
    const wasOnBuyersMarket = isBuyersMarketPath(prevPathnameRef.current);
    if (isBuyersMarketRoute && !wasOnBuyersMarket && !isMarketQueryPaused) {
      handleResfreshMarketResult();
      handleResfreshMarketInfo();
    }
    prevPathnameRef.current = pathname;
  }, [pathname, isMarketQueryPaused, handleResfreshMarketResult, handleResfreshMarketInfo]);

  const marketInfo = marketInfoResult.data?.market as Market | undefined;

  // Refresh both market info and results
  const handleRefresh = useCallback(() => {
    handleResfreshMarketInfo();
    handleResfreshMarketResult();
  }, [handleResfreshMarketInfo, handleResfreshMarketResult]);

  const value = useMemo(
    () => ({
      fetching: infoFetching || resultsFetching,
      filters: marketInfo?.filters,
      infoFetching,
      isMarketQueryPaused,
      marketError: marketInfoResult.error || marketResult.error,
      pageInfo,
      promotions: marketInfo?.promotions,
      sections,
      breakingPointId,
      shipping: marketInfo?.shipping,
      title: marketInfo?.title,
      loadingMarket,
      refresh: handleRefresh,
      refetchResults: handleResfreshMarketResult,
    }),
    [
      infoFetching,
      resultsFetching,
      marketInfo,
      breakingPointId,
      handleRefresh,
      sections,
      marketInfoResult.error,
      marketResult.error,
      handleResfreshMarketResult,
      pageInfo,
      isMarketQueryPaused,
      loadingMarket,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useBuyersMarket = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useBuyersMarket must be used within a BuyersMarketProvider');
  return result;
};
